<script lang="ts">
  import { push } from 'svelte-spa-router';
  import { AuthService, type EmployeeRegistrationData, type CharityRegistrationData, type EmployerRegistrationData } from '../../lib/api/auth.service';
  import { CategoryService, type Category } from '../../lib/api/category.service';
  import { auth } from '../../stores/auth.store';
  import PasswordInput from '../../components/PasswordInput.svelte';
  import SelectField from '../../components/SelectField.svelte';
  import Notification from '../../components/Notification.svelte';
  import { onMount, onDestroy, tick } from 'svelte';

  // Role selection
  let selectedRole: 'employee' | 'charity' | 'employer' | '' = '';
  
  // Common fields
  let email = '';
  let password = '';
  let password_confirmation = '';
  let loading = false;
  let error = '';
  
  // Employee-specific fields
  let full_name = '';
  let phone = '';
  
  // Charity-specific fields
  const ELIGIBLE_CHARITY_DGR_STATUSES = ['dgr1', 'dgr2'] as const;
  type CharityDgrTaxStatus = '' | 'dgr1' | 'dgr2' | 'other';

  const CHARITY_DGR_OPTIONS = [
    { value: 'dgr1', label: 'Australian Charity - DGR Item 1' },
    { value: 'dgr2', label: 'Australian Charity - DGR Item 2' },
    { value: 'other', label: 'Other' },
  ];

  let charity_dgr_tax_status: CharityDgrTaxStatus = '';
  let charity_name = '';
  let category_uuid = '';
  let charity_registration_number = '';
  let contact_name = '';
  let contact_email = '';
  let contact_phone = '';
  let head_office_location = '';
  let website = '';
  let description = '';
  let charity_logo_file: File | null = null;
  let charity_logo_preview = '';
  let charity_logo_input: HTMLInputElement;
  
  // Employer-specific fields
  let employer_name = '';
  let employer_abn = '';
  let employer_contact_name = '';
  let employer_contact_email = '';
  let employer_contact_phone = '';
  let employer_head_office_location = '';
  
  // Categories for charity registration
  let categories: Category[] = [];
  let loadingCategories = false;
  
  // Notification state
  let notificationShow = false;
  let notificationType: 'success' | 'error' = 'success';
  let notificationMessage = '';
  
  // Field-specific errors
  let fieldErrors: Record<string, string[]> = {};
  let validationAttempted = false;
  
  // Track which fields have been touched for validation on blur
  let touchedFields: Set<string> = new Set();
  
  // Step management for multi-step forms (charity and employer)
  let currentStep = 1;
  $: totalSteps = selectedRole === 'charity' ? 3 : 2;
  
  // Add to script section after other state variables
  let terms_and_conditions = false;

  $: registrationTitle = !selectedRole
    ? 'Register Account'
    : selectedRole === 'charity'
      ? 'Charity Registration'
      : selectedRole === 'employer'
        ? 'Employer Registration'
        : 'Employee Registration';

  $: showRegistrationSteps = selectedRole === 'charity' || selectedRole === 'employer';
  $: registrationStepLabel =
    selectedRole === 'charity'
      ? currentStep === 1
        ? 'Before you start'
        : currentStep === 2
          ? 'Basic information'
          : 'Additional details'
      : currentStep === 1
        ? 'Basic information'
        : 'Additional details';
  $: registrationProgressPercent = showRegistrationSteps ? (currentStep / totalSteps) * 100 : 0;
  
  // Load categories when component mounts
  onMount(async () => {
    await loadCategories();
  });
  
  async function loadCategories() {
    loadingCategories = true;
    try {
      const result = await CategoryService.getCategories();
      if (result.success && result.categories) {
        categories = result.categories;
      } else {
      }
    } catch (err) {
    } finally {
      loadingCategories = false;
    }
  }
  
  function showNotification(message: string, type: 'success' | 'error' = 'success') {
    notificationMessage = message;
    notificationType = type;
    notificationShow = true;
  }

  $: charityDgrEligible = ELIGIBLE_CHARITY_DGR_STATUSES.includes(
    charity_dgr_tax_status as (typeof ELIGIBLE_CHARITY_DGR_STATUSES)[number]
  );
  $: charityDgrIneligible = charity_dgr_tax_status !== '' && !charityDgrEligible;
  $: if (selectedRole === 'charity' && currentStep > 1 && !charityDgrEligible) {
    currentStep = 1;
  }

  const CHARITY_DGR_STEP_KEYS = new Set(['dgr_tax_status']);
  const CHARITY_BASIC_STEP_KEYS = new Set([
    'name',
    'category_uuid',
    'charity_registration_number',
    'email',
    'password',
    'password_confirmation',
    'terms',
  ]);
  const EMPLOYER_BASIC_STEP_KEYS = new Set([
    'name',
    'abn',
    'email',
    'password',
    'password_confirmation',
    'terms',
  ]);

  function getCharityStepForFieldKey(fieldKey: string): number {
    if (CHARITY_DGR_STEP_KEYS.has(fieldKey)) return 1;
    if (CHARITY_BASIC_STEP_KEYS.has(fieldKey)) return 2;
    return 3;
  }

  function navigateToStepForValidationErrors(errors: Record<string, string[]>) {
    const keys = Object.keys(errors);
    if (keys.length === 0) return;

    if (selectedRole === 'charity') {
      const targetStep = Math.min(...keys.map(getCharityStepForFieldKey));
      if (currentStep !== targetStep) {
        currentStep = targetStep;
      }
      return;
    }

    if (selectedRole === 'employer' && currentStep === 2) {
      const hasBasicStepError = keys.some((key) => EMPLOYER_BASIC_STEP_KEYS.has(key));
      if (hasBasicStepError) {
        currentStep = 1;
      }
    }
  }

  function getValidationFieldKey(fieldName: string): string {
    if (fieldName === 'charity_name' || fieldName === 'employer_name') return 'name';
    if (fieldName === 'employer_abn') return 'abn';
    if (fieldName === 'employer_contact_name') return 'contact_name';
    if (fieldName === 'employer_contact_email') return 'contact_email';
    if (fieldName === 'employer_contact_phone') return 'contact_phone';
    if (fieldName === 'employer_head_office_location') return 'head_office_location';
    return fieldName;
  }

  function normalizeApiFieldErrors(errors: Record<string, string[]>): Record<string, string[]> {
    const normalized: Record<string, string[]> = {};
    for (const [key, messages] of Object.entries(errors)) {
      const normalizedKey = key === 'terms_and_conditions' ? 'terms' : key;
      normalized[normalizedKey] = messages;
    }
    return normalized;
  }

  function getFieldElementId(fieldKey: string): string | null {
    const ids: Record<string, string> = {
      full_name: 'full_name',
      name: selectedRole === 'charity' ? 'charity_name' : 'employer_name',
      phone: 'phone',
      email: 'email',
      password: 'password',
      password_confirmation: 'passwordConfirmation',
      charity_registration_number: 'charity_registration_number',
      dgr_tax_status: 'charity_dgr_tax_status',
      category_uuid: 'category',
      terms:
        selectedRole === 'employee'
          ? 'terms-checkbox-employee'
          : selectedRole === 'employer'
            ? 'terms-checkbox-employer'
            : 'terms-checkbox',
      abn: 'employer_abn',
      contact_name: selectedRole === 'employer' ? 'employer_contact_name' : 'contact_name',
      contact_email: selectedRole === 'employer' ? 'employer_contact_email' : 'contact_email',
      contact_phone: selectedRole === 'employer' ? 'employer_contact_phone' : 'contact_phone',
      head_office_location:
        selectedRole === 'employer' ? 'employer_head_office_location' : 'head_office_location',
      website: 'website',
      description: 'description',
      logo: 'charity-logo',
    };
    return ids[fieldKey] ?? fieldKey;
  }

  async function scrollToField(fieldKey: string) {
    if (showRegistrationSteps) {
      navigateToStepForValidationErrors({ [fieldKey]: [''] });
      await tick();
    }

    const elementId = getFieldElementId(fieldKey);
    const el = elementId ? document.getElementById(elementId) : null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
      return;
    }

    const firstInvalid = document.querySelector('[aria-invalid="true"]');
    if (firstInvalid instanceof HTMLElement) {
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstInvalid.focus({ preventScroll: true });
    }
  }

  async function scrollToFirstError() {
    const firstKey = Object.keys(fieldErrors)[0];
    if (firstKey) {
      await scrollToField(firstKey);
    }
  }

  function commitValidationErrors(errors: Record<string, string[]>) {
    fieldErrors = { ...errors };
    validationAttempted = Object.keys(errors).length > 0;

    if (showRegistrationSteps) {
      navigateToStepForValidationErrors(errors);
    }
  }

  function queueScrollToFirstError() {
    if (Object.keys(fieldErrors).length === 0) return;
    tick().then(() => scrollToFirstError());
  }

  function applyValidationErrors(errors: Record<string, string[]>) {
    commitValidationErrors(errors);
    queueScrollToFirstError();
  }

  $: validationErrors = fieldErrors;
  
  function handleApiError(errorResponse: any) {
    fieldErrors = {};

    let apiErrors: Record<string, string[]> | null = null;
    let generalMessage: string | null = null;

    if (errorResponse?.response?.data) {
      const apiError = errorResponse.response.data;
      if (apiError.data?.errors) {
        apiErrors = normalizeApiFieldErrors(apiError.data.errors);
      } else if (apiError.message) {
        generalMessage = apiError.message;
      }
    } else if (errorResponse?.data?.errors) {
      apiErrors = normalizeApiFieldErrors(errorResponse.data.errors);
    } else if (errorResponse?.message) {
      generalMessage = errorResponse.message;
    }

    if (apiErrors && Object.keys(apiErrors).length > 0) {
      applyValidationErrors(apiErrors);
    } else if (generalMessage) {
      showNotification(generalMessage, 'error');
    } else {
      showNotification('Registration failed. Please try again.', 'error');
    }
  }
  
  function getFieldError(fieldName: string): string | null {
    return validationErrors[fieldName]?.[0] ?? null;
  }

  function hasFieldError(fieldName: string): boolean {
    return Boolean(validationErrors[fieldName]?.[0]);
  }
  
  function handleFieldBlur(fieldName: string, value: string) {
    touchedFields.add(fieldName);
    
    // Validate the specific field on blur
    let error: string | null = null;
    
    switch (fieldName) {
      case 'email':
        error = validateEmail(value);
        break;
      case 'password':
        error = validatePassword(value);
        break;
      case 'password_confirmation':
        error = validatePasswordConfirmation(password, value);
        break;
      case 'full_name':
        if (selectedRole === 'employee') {
          error = validateRequired(value, 'Full name');
        }
        break;
      case 'phone':
        if (selectedRole === 'employee') {
          error = validatePhone(value);
        }
        break;
      case 'charity_name':
      case 'employer_name':
        error = validateRequired(value, fieldName === 'charity_name' ? 'Charity name' : 'Company name');
        break;
      case 'charity_registration_number':
        error = validateCharityRegistrationNumber(value);
        break;
      case 'abn':
      case 'employer_abn':
        error = validateABN(value);
        break;
      case 'category_uuid':
        if (selectedRole === 'charity') {
          error = validateRequired(value, 'Category');
        }
        break;
      case 'contact_email':
      case 'employer_contact_email':
        if (value) {
          error = validateEmail(value);
        }
        break;
      case 'contact_phone':
      case 'employer_contact_phone':
        if (value) {
          error = validatePhone(value);
        }
        break;
      case 'website':
        if (value) {
          error = validateURL(value);
        }
        break;
    }
    
    // Update field errors
    const errorKey = getValidationFieldKey(fieldName);
    if (error) {
      fieldErrors[errorKey] = [error];
      fieldErrors = { ...fieldErrors };
    } else {
      // Clear error for this field if it's valid
      if (fieldErrors[errorKey]) {
        delete fieldErrors[errorKey];
        fieldErrors = { ...fieldErrors }; // Trigger reactivity
      }
    }
  }
  
  function clearFieldError(fieldName: string) {
    if (fieldErrors[fieldName]) {
      delete fieldErrors[fieldName];
      fieldErrors = { ...fieldErrors };
      if (Object.keys(fieldErrors).length === 0) {
        validationAttempted = false;
      }
    }
  }
  
  function handleRoleChange() {
    // Clear form fields when role changes
    clearFormFields();
    clearErrors();
  }
  
  function selectRole(role: 'employee' | 'charity' | 'employer') {
    selectedRole = role;
    clearFormFields();
    clearErrors();
  }
  
  function goBackToRoleSelection() {
    selectedRole = '';
    currentStep = 1;
    clearFormFields();
    clearErrors();
  }
  
  function nextStep() {
    if (currentStep < totalSteps) {
      currentStep++;
    }
  }
  
  function previousStep() {
    if (currentStep > 1) {
      currentStep--;
    }
  }
  
  function validateCharityDgrStep(): boolean {
    const errors: Record<string, string[]> = {};
    const dgrError = validateCharityDgrTaxStatus(charity_dgr_tax_status);
    if (dgrError) errors.dgr_tax_status = [dgrError];

    if (Object.keys(errors).length > 0) {
      applyValidationErrors(errors);
      return false;
    }

    return charityDgrEligible;
  }

  function validateCharityBasicStep(): boolean {
    const errors: Record<string, string[]> = {};

    const nameError = validateRequired(charity_name, 'Charity name');
    if (nameError) errors.name = [nameError];

    const categoryError = validateRequired(category_uuid, 'Category');
    if (categoryError) errors.category_uuid = [categoryError];

    const regError = validateCharityRegistrationNumber(charity_registration_number);
    if (regError) errors.charity_registration_number = [regError];

    const emailError = validateEmail(email);
    if (emailError) errors.email = [emailError];

    const passwordError = validatePassword(password);
    if (passwordError) errors.password = [passwordError];

    const passwordConfirmationError = validatePasswordConfirmation(password, password_confirmation);
    if (passwordConfirmationError) errors.password_confirmation = [passwordConfirmationError];

    if (!terms_and_conditions) {
      errors.terms = ['You must accept the Terms and Conditions to register'];
    }

    if (Object.keys(errors).length > 0) {
      applyValidationErrors(errors);
      return false;
    }

    return true;
  }

  function validateStep1(): boolean {
    const errors: Record<string, string[]> = {};
    
    if (selectedRole === 'employer') {
      const nameError = validateRequired(employer_name, 'Company name');
      if (nameError) errors.name = [nameError];
      
      const abnError = validateABN(employer_abn);
      if (abnError) errors.abn = [abnError];
    }
    
    // Validate common fields
    const emailError = validateEmail(email);
    if (emailError) errors.email = [emailError];
    
    const passwordError = validatePassword(password);
    if (passwordError) errors.password = [passwordError];
    
    const passwordConfirmationError = validatePasswordConfirmation(password, password_confirmation);
    if (passwordConfirmationError) errors.password_confirmation = [passwordConfirmationError];
    
    if (!terms_and_conditions) {
      errors.terms = ['You must accept the Terms and Conditions to register'];
    }
    
    if (Object.keys(errors).length > 0) {
      applyValidationErrors(errors);
      return false;
    }
    
    return true;
  }
  
  function clearFormFields() {
    email = '';
    password = '';
    password_confirmation = '';
    full_name = '';
    phone = '';
    charity_dgr_tax_status = '';
    charity_name = '';
    category_uuid = '';
    charity_registration_number = '';
    contact_name = '';
    contact_email = '';
    contact_phone = '';
    head_office_location = '';
    website = '';
    description = '';
    resetCharityLogo();
    employer_name = '';
    employer_abn = '';
    employer_contact_name = '';
    employer_contact_email = '';
    employer_contact_phone = '';
    employer_head_office_location = '';
    touchedFields.clear();
  }
  
  function clearErrors() {
    error = '';
    fieldErrors = {};
    validationAttempted = false;
  }
  
  // Client-side validation functions
  function validateEmail(email: string): string | null {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return null;
  }
  
  function validatePassword(password: string): string | null {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters long';
    return null;
  }
  
  function validatePasswordConfirmation(password: string, confirmation: string): string | null {
    if (!confirmation) return 'Password confirmation is required';
    if (password !== confirmation) return 'Passwords do not match';
    return null;
  }
  
  function validatePhone(phone: string): string | null {
    if (!phone) return 'Phone number is required';
    // Basic phone validation - can be customized for specific formats
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,}$/;
    if (!phoneRegex.test(phone)) return 'Please enter a valid phone number';
    return null;
  }
  
  function validateABN(abn: string): string | null {
    if (!abn) return 'ABN is required';
    const cleanABN = abn.replace(/\s/g, '');
    if (cleanABN.length !== 11) return 'ABN must be 11 digits';
    if (!/^\d+$/.test(cleanABN)) return 'ABN must contain only numbers';
    return null;
  }

  function validateCharityDgrTaxStatus(status: CharityDgrTaxStatus): string | null {
    if (!status) return 'Please select your charity tax status';
    if (!ELIGIBLE_CHARITY_DGR_STATUSES.includes(status as (typeof ELIGIBLE_CHARITY_DGR_STATUSES)[number])) {
      return "This tax status isn't supported on Leave2Give. Select DGR Item 1 or DGR Item 2 to continue.";
    }
    return null;
  }

  function validateCharityRegistrationNumber(value: string): string | null {
    if (!value) return 'ABN is required';
    const clean = value.replace(/\s/g, '');
    if (clean.length !== 11) return 'ABN must be 11 digits';
    if (!/^\d+$/.test(clean)) return 'ABN must contain only numbers';
    return null;
  }

  function revokeCharityLogoPreview() {
    if (charity_logo_preview) {
      URL.revokeObjectURL(charity_logo_preview);
      charity_logo_preview = '';
    }
  }

  function resetCharityLogo() {
    charity_logo_file = null;
    revokeCharityLogoPreview();
    if (charity_logo_input) {
      charity_logo_input.value = '';
    }
  }

  function openCharityLogoPicker() {
    charity_logo_input?.click();
  }

  function handleCharityLogoChange(event: Event) {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0] || null;
    delete fieldErrors.logo;
    fieldErrors = { ...fieldErrors };

    if (!file) {
      resetCharityLogo();
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const maxBytes = 2 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      fieldErrors = { ...fieldErrors, logo: ['Please upload a JPG, PNG, or WebP logo.'] };
      resetCharityLogo();
      return;
    }

    if (file.size > maxBytes) {
      fieldErrors = { ...fieldErrors, logo: ['Logo must be smaller than 2 MB.'] };
      resetCharityLogo();
      return;
    }

    revokeCharityLogoPreview();
    charity_logo_file = file;
    charity_logo_preview = URL.createObjectURL(file);
  }

  onDestroy(() => {
    revokeCharityLogoPreview();
  });
  
  function validateURL(url: string): string | null {
    if (!url) return null; // URL is optional
    try {
      // Accept URLs with or without http:// or https://
      // Also accept www.xxx.com.au format
      let urlToValidate = url.trim();
      if (!urlToValidate.startsWith('http://') && !urlToValidate.startsWith('https://')) {
        urlToValidate = `https://${urlToValidate}`;
      }
      new URL(urlToValidate);
      return null;
    } catch {
      return 'Please enter a valid URL (e.g., www.example.com or https://example.com)';
    }
  }
  
  function validateRequired(value: string, fieldName: string): string | null {
    if (!value || value.trim() === '') {
      return `${fieldName} is required`;
    }
    return null;
  }
  
  function validateForm(): boolean {
    const errors: Record<string, string[]> = {};
    
    // Validate common fields
    const emailError = validateEmail(email);
    if (emailError) errors.email = [emailError];
    
    const passwordError = validatePassword(password);
    if (passwordError) errors.password = [passwordError];
    
    const passwordConfirmationError = validatePasswordConfirmation(password, password_confirmation);
    if (passwordConfirmationError) errors.password_confirmation = [passwordConfirmationError];
    
    // Role-specific validation
    if (selectedRole === 'employee') {
      const nameError = validateRequired(full_name, 'Full name');
      if (nameError) errors.full_name = [nameError];
      
      const phoneError = validatePhone(phone);
      if (phoneError) errors.phone = [phoneError];
    }
    
    if (selectedRole === 'charity') {
      const dgrError = validateCharityDgrTaxStatus(charity_dgr_tax_status);
      if (dgrError) errors.dgr_tax_status = [dgrError];

      const nameError = validateRequired(charity_name, 'Charity name');
      if (nameError) errors.name = [nameError];
      
      const categoryError = validateRequired(category_uuid, 'Category');
      if (categoryError) errors.category_uuid = [categoryError];
      
      const regError = validateCharityRegistrationNumber(charity_registration_number);
      if (regError) errors.charity_registration_number = [regError];
      
      // Optional field validations
      if (contact_email) {
        const contactEmailError = validateEmail(contact_email);
        if (contactEmailError) errors.contact_email = [contactEmailError];
      }
      
      if (contact_phone) {
        const contactPhoneError = validatePhone(contact_phone);
        if (contactPhoneError) errors.contact_phone = [contactPhoneError];
      }
      
      if (website) {
        const websiteError = validateURL(website);
        if (websiteError) errors.website = [websiteError];
      }
    }
    
    if (selectedRole === 'employer') {
      const nameError = validateRequired(employer_name, 'Company name');
      if (nameError) errors.name = [nameError];
      
      const abnError = validateABN(employer_abn);
      if (abnError) errors.abn = [abnError];
      
      // Optional field validations
      if (employer_contact_email) {
        const contactEmailError = validateEmail(employer_contact_email);
        if (contactEmailError) errors.contact_email = [contactEmailError];
      }
      
      if (employer_contact_phone) {
        const contactPhoneError = validatePhone(employer_contact_phone);
        if (contactPhoneError) errors.contact_phone = [contactPhoneError];
      }
    }
    
    // Add to validateForm function before the return statement
    if (!terms_and_conditions) {
      errors.terms = ['You must accept the Terms and Conditions to register'];
    }
    
    if (Object.keys(errors).length > 0) {
      applyValidationErrors(errors);
      return false;
    }
    
    return true;
  }
  
  async function handleRegister() {
    if (!selectedRole) {
      showNotification('Please select a role', 'error');
      return;
    }
    
    // Clear previous errors
    clearErrors();
    
    // Perform client-side validation
    if (!validateForm()) {
      return; // Stop if validation fails
    }
    
    loading = true;
    
    try {
      let result;
      
      switch (selectedRole) {
        case 'employee':
          result = await registerEmployee();
          break;
        case 'charity':
          result = await registerCharity();
          break;
        case 'employer':
          result = await registerEmployer();
          break;
        default:
          throw new Error('Invalid role selected');
      }

      if (result.success) {        
        localStorage.setItem('userEmail', email);
        showNotification('Registration successful! Please check your email for verification.', 'success');
        setTimeout(() => {
        push('/otp-verification');
        }, 1500);
      } else {
        // Handle the case where result has an error property
        const errorToHandle = (result as any).error || result;
        handleApiError(errorToHandle);
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      loading = false;
    }
  }
  
  async function registerEmployee() {
    // Client-side validation is already done, so we can proceed directly
    const userData: EmployeeRegistrationData = {
      full_name,
      email,
      phone,
      password,
      password_confirmation, 
      terms_and_conditions
    };
    
    return await AuthService.registerEmployee(userData);
  }
  
  async function registerCharity() {
    // Client-side validation is already done, so we can proceed directly
    // Normalize website URL - add https:// if not present
    let normalizedWebsite = website;
    if (website && !website.startsWith('http://') && !website.startsWith('https://')) {
      normalizedWebsite = `https://${website}`;
    }
    
    const userData: CharityRegistrationData = {
      name: charity_name,
      email,
      category_uuid,
      dgr_tax_status: charity_dgr_tax_status as 'dgr1' | 'dgr2',
      charity_registration_number: charity_registration_number.replace(/\s/g, ''),
      password,
      password_confirmation,
      terms_and_conditions,
      ...(contact_name && { contact_name }),
      ...(contact_email && { contact_email }),
      ...(contact_phone && { contact_phone }),
      ...(head_office_location && { head_office_location }),
      ...(normalizedWebsite && { website: normalizedWebsite }),
      ...(description && { description })
    };
    
    return await AuthService.registerCharity(userData, charity_logo_file);
  }
  
  async function registerEmployer() {
    // Client-side validation is already done, so we can proceed directly
    const userData: EmployerRegistrationData = {
      name: employer_name,
      email,
      abn: employer_abn,
      password,
      password_confirmation,
      terms_and_conditions,
      ...(employer_contact_name && { contact_name: employer_contact_name }),
      ...(employer_contact_email && { contact_email: employer_contact_email }),
      ...(employer_contact_phone && { contact_phone: employer_contact_phone }),
      ...(employer_head_office_location && { head_office_location: employer_head_office_location })
    };
    
    return await AuthService.registerEmployer(userData);
  }
</script>

<main class:registration-page={!!selectedRole}>
  <div
    class="form-container"
    class:employee-form-container={selectedRole === 'employee'}
    class:registration-active={!!selectedRole}
  >
    {#if selectedRole}
      <header class="registration-header">
        <button
          type="button"
          class="registration-back-link"
          on:click={goBackToRoleSelection}
          disabled={loading}
          aria-label="Go back to role selection"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7"></path>
          </svg>
          Change role
        </button>

        <div class="registration-header-brand">
          <div class="registration-header-logo-wrap" aria-hidden="true">
            <img
              src="https://give.leave2give.com.au/logo.svg"
              alt=""
              class="registration-header-logo"
            />
          </div>
          <div class="registration-header-text">
            <h1>{registrationTitle}</h1>
            {#if showRegistrationSteps}
              <p class="registration-step-meta" aria-live="polite">
                <span class="registration-step-pill">Step {currentStep} of {totalSteps}</span>
                <span class="registration-step-label">{registrationStepLabel}</span>
              </p>
            {/if}
          </div>
        </div>

        {#if showRegistrationSteps}
          <div
            class="registration-progress"
            role="progressbar"
            aria-valuenow={currentStep}
            aria-valuemin={1}
            aria-valuemax={totalSteps}
            aria-label="Registration progress"
          >
            <div class="registration-progress-fill" style="width: {registrationProgressPercent}%"></div>
          </div>
        {/if}
      </header>
    {:else}
      <div class="registration-brand">
        <div class="logo-container">
          <img src="https://give.leave2give.com.au/logo.svg" alt="Leave2Give Logo" class="logo-image" />
        </div>
        <h1>{registrationTitle}</h1>
      </div>
      <p class="subtitle">Create an account to start using Leave2Give</p>
    {/if}
    
    {#if !selectedRole}
      <!-- Role Selection Tiles -->
      <div class="role-selection">
        <h2 class="role-selection-title">I am registering as</h2>
        <div class="role-tiles">
          <button
            type="button"
            class="role-tile"
            on:click={() => selectRole('employee')}
            on:keydown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                selectRole('employee');
              }
            }}
            disabled={loading}
            aria-label="Select Employee role"
          >
            <div class="role-tile-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
            <h3 class="role-tile-title">Employee</h3>
            <p class="role-tile-description">Register as an individual employee</p>
          </button>
          
          <button
            type="button"
            class="role-tile"
            on:click={() => selectRole('charity')}
            on:keydown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                selectRole('charity');
              }
            }}
            disabled={loading}
            aria-label="Select Charity Organization role"
          >
            <div class="role-tile-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
            </div>
            <h3 class="role-tile-title">Charity Organization</h3>
            <p class="role-tile-description">Register your charity organization</p>
          </button>
          
          <button
            type="button"
            class="role-tile"
            on:click={() => selectRole('employer')}
            on:keydown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                selectRole('employer');
              }
            }}
            disabled={loading}
            aria-label="Select Employer role"
          >
            <div class="role-tile-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="9" y1="3" x2="9" y2="21"></line>
                <line x1="3" y1="9" x2="21" y2="9"></line>
              </svg>
            </div>
            <h3 class="role-tile-title">Employer</h3>
            <p class="role-tile-description">Register your company as an employer</p>
          </button>
        </div>
        
        <!-- Login Link on Role Selection Screen -->
        <div class="login-link">
          Already have an account? <a href="/login" on:click|preventDefault={() => push('/login')}>Login</a>
        </div>
      </div>
    {:else}
      <!-- Registration Form -->
      <form
        on:submit|preventDefault={handleRegister}
        class:employee-form={selectedRole === 'employee'}
        class:multi-step-form={showRegistrationSteps}
      >
        {#if error}
          <div class="form-error-banner" role="alert">
            {error}
          </div>
        {/if}
        
        <!-- Employee Fields -->
        {#if selectedRole === 'employee'}
          <div class="employee-form-fields">
            <div class="form-group">
              <label for="full_name">Full Name <span class="required" aria-label="required">*</span></label>
              <input 
                type="text" 
                id="full_name" 
                bind:value={full_name} 
                required 
                disabled={loading}
                placeholder="Enter your full name"
                autocomplete="name"
                aria-invalid={Boolean(validationErrors.full_name)}
                aria-describedby={validationErrors.full_name ? 'full_name-error' : undefined}
                class:field-invalid={Boolean(validationErrors.full_name)}
                on:blur={() => handleFieldBlur('full_name', full_name)}
                on:input={() => clearFieldError('full_name')}
              />
              {#if validationErrors.full_name}
                <div class="field-error" id="full_name-error" role="alert">{validationErrors.full_name[0]}</div>
              {/if}
            </div>

            <div class="form-group">
              <label for="phone">Contact Number <span class="required" aria-label="required">*</span></label>
              <input 
                type="tel" 
                id="phone" 
                bind:value={phone} 
                required 
                disabled={loading}
                placeholder="00 000000000"
                autocomplete="tel"
                aria-invalid={Boolean(validationErrors.phone)}
                aria-describedby={validationErrors.phone ? 'phone-error' : undefined}
                class:field-invalid={Boolean(validationErrors.phone)}
                on:blur={() => handleFieldBlur('phone', phone)}
                on:input={() => clearFieldError('phone')}
              />
              {#if validationErrors.phone}
                <div class="field-error" id="phone-error" role="alert">{validationErrors.phone[0]}</div>
              {/if}
            </div>
            
            <!-- Common Fields for Employee -->
            <div class="form-group">
              <label for="email">Email Address <span class="required" aria-label="required">*</span></label>
              <input 
                type="email" 
                id="email" 
                bind:value={email} 
                required 
                disabled={loading}
                placeholder="Enter your email"
                autocomplete="email"
                aria-invalid={Boolean(validationErrors.email)}
                aria-describedby={validationErrors.email ? 'email-error' : undefined}
                class:field-invalid={Boolean(validationErrors.email)}
                on:blur={() => handleFieldBlur('email', email)}
                on:input={() => clearFieldError('email')}
              />
              {#if validationErrors.email}
                <div class="field-error" id="email-error" role="alert">{validationErrors.email[0]}</div>
              {/if}
            </div>
            
            <!-- Password Fields Side by Side -->
            <div class="password-fields-row">
              <div class="form-group password-group">
                <label for="password">Create Password <span class="required" aria-label="required">*</span></label>
                <PasswordInput 
                  id="password"
                  bind:value={password}
                  required={true}
                  disabled={loading}
                  placeholder="At least 8 characters"
                  autocomplete="new-password"
                  ariaInvalid={Boolean(validationErrors.password)}
                  ariaDescribedby={validationErrors.password ? 'password-error' : 'password-help'}
                  className={Boolean(validationErrors.password) ? 'field-invalid' : ''}
                  on:blur={() => handleFieldBlur('password', password)}
                  on:input={() => clearFieldError('password')}
                />
                <div class="password-help-text" id="password-help">Password must be at least 8 characters long</div>
                {#if validationErrors.password}
                  <div class="field-error" id="password-error" role="alert">{validationErrors.password[0]}</div>
                {/if}
              </div>
              
              <div class="form-group password-group">
                <label for="passwordConfirmation">Confirm Password <span class="required" aria-label="required">*</span></label>
                <PasswordInput 
                  id="passwordConfirmation"
                  bind:value={password_confirmation}
                  required={true}
                  disabled={loading}
                  placeholder="Re-enter password"
                  autocomplete="new-password"
                  ariaInvalid={Boolean(validationErrors.password_confirmation)}
                  ariaDescribedby={validationErrors.password_confirmation ? 'password_confirmation-error' : undefined}
                  className={Boolean(validationErrors.password_confirmation) ? 'field-invalid' : ''}
                  on:blur={() => handleFieldBlur('password_confirmation', password_confirmation)}
                  on:input={() => clearFieldError('password_confirmation')}
                />
                {#if validationErrors.password_confirmation}
                  <div class="field-error" id="password_confirmation-error" role="alert">{validationErrors.password_confirmation[0]}</div>
                {/if}
              </div>
            </div>
          
          <!-- Terms and Conditions for Employee -->
          <div class="form-group terms-group">
            <label class="checkbox-label" for="terms-checkbox-employee">
              <div class="custom-checkbox">
                <input 
                  type="checkbox" 
                  id="terms-checkbox-employee"
                  bind:checked={terms_and_conditions}
                  disabled={loading}
                  on:change={() => { if (terms_and_conditions) clearFieldError('terms'); }}
                  aria-invalid={Boolean(validationErrors.terms)}
                  aria-describedby={validationErrors.terms ? 'terms-error-employee' : undefined}
                  class:field-invalid={Boolean(validationErrors.terms)}
                />
                <span class="checkmark"></span>
              </div>
              <span class="terms-text">I agree to the <a href="https://www.leave2give.com.au/terms-conditions.html" target="_blank" rel="noopener noreferrer">Leave2Give Terms and Conditions</a></span>
            </label>
            {#if validationErrors.terms}
              <div class="field-error" id="terms-error-employee" role="alert">{validationErrors.terms[0]}</div>
            {/if}
          </div>
          </div>
        {/if}
        
        <!-- Charity Fields -->
        {#if selectedRole === 'charity'}
          {#if currentStep === 1}
            <div class="charity-dgr-step-fields">
              <section class="form-section" aria-labelledby="charity-dgr-heading">
                <h2 id="charity-dgr-heading" class="form-section-title">Before you start</h2>
                <div class="form-section-body">
                  <p class="step-intro charity-dgr-intro">
                    Only charities with Deductible Gift Recipient (DGR) Item 1 or Item 2 status in Australia can register on Leave2Give. Workplace giving donations are only tax deductible for eligible DGR charities.
                  </p>
                  <div class="form-group">
                    <label for="charity_dgr_tax_status">Charity tax status <span class="required" aria-label="required">*</span></label>
                    <SelectField
                      id="charity_dgr_tax_status"
                      bind:value={charity_dgr_tax_status}
                      options={CHARITY_DGR_OPTIONS}
                      placeholder="Select your charity tax status"
                      disabled={loading}
                      invalid={Boolean(validationErrors.dgr_tax_status)}
                      ariaDescribedby={validationErrors.dgr_tax_status ? 'charity_dgr_tax_status-error charity_dgr_tax_status-hint' : 'charity_dgr_tax_status-hint'}
                      on:change={() => clearFieldError('dgr_tax_status')}
                    />
                    {#if validationErrors.dgr_tax_status}
                      <div class="field-error" id="charity_dgr_tax_status-error" role="alert">{validationErrors.dgr_tax_status[0]}</div>
                    {/if}
                  </div>
                  <p class="field-hint" id="charity_dgr_tax_status-hint">
                    If you're not sure what your DGR status is,
                    <a href="https://abr.business.gov.au/Tools/DgrListing" target="_blank" rel="noopener noreferrer">check here</a>.
                  </p>
                  {#if charityDgrIneligible}
                    <div class="dgr-ineligible-notice" role="alert">
                      <p class="dgr-ineligible-notice__lead">Registration isn't available for this tax status.</p>
                      <p>Leave2Give is limited to Australian charities with:</p>
                      <ul>
                        <li>DGR Item 1 endorsement, or</li>
                        <li>DGR Item 2 endorsement</li>
                      </ul>
                      <p>That keeps workplace giving donations tax deductible for employees.</p>
                      <p class="dgr-ineligible-notice__footer">If your status changes, you're welcome to try again.</p>
                    </div>
                  {/if}
                </div>
              </section>

              <div class="step-navigation">
                {#if charityDgrEligible}
                  <button type="button" class="btn-next" on:click={() => { if (validateCharityDgrStep()) nextStep(); }}>
                    Next: Basic Information
                  </button>
                {/if}
              </div>
            </div>
          {:else if currentStep === 2}
            <div class="charity-step1-fields">
              <section class="form-section" aria-labelledby="charity-org-heading">
                <h2 id="charity-org-heading" class="form-section-title">Organisation details</h2>
                <div class="form-section-body">
              <div class="charity-identity-block">
              <div class="charity-identity-row">
                <div class="charity-logo-picker">
                  <button
                    type="button"
                    class="charity-logo-thumb"
                    class:has-logo={!!charity_logo_preview}
                    on:click={openCharityLogoPicker}
                    disabled={loading}
                    aria-label="Upload charity logo"
                  >
                    {#if charity_logo_preview}
                      <img src={charity_logo_preview} alt="{charity_name || 'Charity'} logo preview" />
                    {:else}
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                      <span>Logo</span>
                    {/if}
                  </button>
                  {#if charity_logo_file}
                    <button type="button" class="charity-logo-remove" on:click={resetCharityLogo} disabled={loading}>
                      Remove
                    </button>
                  {/if}
                  <input
                    bind:this={charity_logo_input}
                    id="charity-logo"
                    class="logo-file-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    disabled={loading}
                    on:change={handleCharityLogoChange}
                  />
                </div>

                <div class="form-group charity-name-field">
                  <label for="charity_name">Charity Name <span class="required" aria-label="required">*</span></label>
                  <input 
                    type="text" 
                    id="charity_name" 
                    bind:value={charity_name} 
                    required 
                    disabled={loading}
                    placeholder="Enter charity name"
                    autocomplete="organization"
                    aria-invalid={Boolean(validationErrors.name)}
                    aria-describedby={validationErrors.name ? 'charity_name-error' : undefined}
                    class:field-invalid={Boolean(validationErrors.name)}
                    on:blur={() => handleFieldBlur('charity_name', charity_name)}
                    on:input={() => clearFieldError('name')}
                  />
                  {#if validationErrors.name}
                    <div class="field-error" id="charity_name-error" role="alert">{validationErrors.name[0]}</div>
                  {/if}
                </div>
              </div>
              <p class="field-hint field-hint-inline" id="charity-logo-label">
                Logo is optional — you'll see a preview here. Square images work best. JPG, PNG, or WebP up to 2 MB. You can update it anytime in your profile.
              </p>
              {#if validationErrors.logo}
                <div class="field-error" id="charity-logo-error" role="alert">{validationErrors.logo[0]}</div>
              {/if}
              </div>

              <div class="form-group">
                <label for="email">Email Address <span class="required" aria-label="required">*</span></label>
                <input 
                  type="email" 
                  id="email" 
                  bind:value={email} 
                  required 
                  disabled={loading}
                  placeholder="Enter your email"
                  autocomplete="email"
                  aria-invalid={Boolean(validationErrors.email)}
                  aria-describedby={validationErrors.email ? 'email-error' : undefined}
                  class:field-invalid={Boolean(validationErrors.email)}
                  on:blur={() => handleFieldBlur('email', email)}
                  on:input={() => clearFieldError('email')}
                />
                {#if validationErrors.email}
                  <div class="field-error" id="email-error" role="alert">{validationErrors.email[0]}</div>
                {/if}
              </div>

              <div class="form-fields-row">
                <div class="form-group">
                  <label for="charity_registration_number">ABN <span class="required" aria-label="required">*</span></label>
                  <input 
                    type="text" 
                    id="charity_registration_number" 
                    bind:value={charity_registration_number} 
                    required 
                    disabled={loading}
                    placeholder="11-digit ABN"
                    autocomplete="off"
                    aria-invalid={Boolean(validationErrors.charity_registration_number)}
                    aria-describedby={validationErrors.charity_registration_number ? 'charity_registration_number-error charity_registration_number-hint' : 'charity_registration_number-hint'}
                    class:field-invalid={Boolean(validationErrors.charity_registration_number)}
                    on:blur={() => handleFieldBlur('charity_registration_number', charity_registration_number)}
                    on:input={() => clearFieldError('charity_registration_number')}
                  />
                  {#if validationErrors.charity_registration_number}
                    <div class="field-error" id="charity_registration_number-error" role="alert">{validationErrors.charity_registration_number[0]}</div>
                  {/if}
                </div>

                <div class="form-group">
                  <label for="category">Category <span class="required" aria-label="required">*</span></label>
                  <div class="select-wrapper">
                    <select 
                      id="category" 
                      bind:value={category_uuid} 
                      required 
                      disabled={loading || loadingCategories}
                      aria-invalid={Boolean(validationErrors.category_uuid)}
                      aria-describedby={validationErrors.category_uuid ? 'category-error' : undefined}
                      class:field-invalid={Boolean(validationErrors.category_uuid)}
                      on:change={() => clearFieldError('category_uuid')}
                    >
                      <option value="">Select category...</option>
                      {#each categories as category}
                        <option value={category.uuid}>{category.name}</option>
                      {/each}
                    </select>
                  </div>
                  {#if validationErrors.category_uuid}
                    <div class="field-error" id="category-error" role="alert">{validationErrors.category_uuid[0]}</div>
                  {/if}
                </div>
              </div>
              <p class="field-hint field-hint-full" id="charity_registration_number-hint">
                Look up your ABN at <a href="https://www.abr.business.gov.au/" target="_blank" rel="noopener noreferrer">abr.business.gov.au</a>.
              </p>
                </div>
              </section>

              <section class="form-section" aria-labelledby="charity-account-heading">
                <h2 id="charity-account-heading" class="form-section-title">Account security</h2>
                <div class="form-section-body">
              
              <div class="password-fields-row">
                <div class="form-group password-group">
                  <label for="password">Create Password <span class="required" aria-label="required">*</span></label>
                  <PasswordInput 
                    id="password"
                    bind:value={password}
                    required={true}
                    disabled={loading}
                    placeholder="At least 8 characters"
                    autocomplete="new-password"
                    ariaInvalid={Boolean(validationErrors.password)}
                    ariaDescribedby={validationErrors.password ? 'password-error' : 'password-help'}
                    className={Boolean(validationErrors.password) ? 'field-invalid' : ''}
                    on:blur={() => handleFieldBlur('password', password)}
                    on:input={() => clearFieldError('password')}
                  />
                  <div class="password-help-text" id="password-help">Password must be at least 8 characters long</div>
                  {#if validationErrors.password}
                    <div class="field-error" id="password-error" role="alert">{validationErrors.password[0]}</div>
                  {/if}
                </div>
                
                <div class="form-group password-group">
                  <label for="passwordConfirmation">Confirm Password <span class="required" aria-label="required">*</span></label>
                  <PasswordInput 
                    id="passwordConfirmation"
                    bind:value={password_confirmation}
                    required={true}
                    disabled={loading}
                    placeholder="Re-enter password"
                    autocomplete="new-password"
                    ariaInvalid={Boolean(validationErrors.password_confirmation)}
                    ariaDescribedby={validationErrors.password_confirmation ? 'password_confirmation-error' : undefined}
                    className={Boolean(validationErrors.password_confirmation) ? 'field-invalid' : ''}
                    on:blur={() => handleFieldBlur('password_confirmation', password_confirmation)}
                    on:input={() => clearFieldError('password_confirmation')}
                  />
                  {#if validationErrors.password_confirmation}
                    <div class="field-error" id="password_confirmation-error" role="alert">{validationErrors.password_confirmation[0]}</div>
                  {/if}
                </div>
              </div>
              
              <!-- Terms and Conditions -->
              <div class="form-group terms-group">
                <label class="checkbox-label" for="terms-checkbox">
                  <div class="custom-checkbox">
                    <input 
                      type="checkbox" 
                      id="terms-checkbox"
                      bind:checked={terms_and_conditions}
                      disabled={loading}
                      on:change={() => { if (terms_and_conditions) clearFieldError('terms'); }}
                      aria-invalid={Boolean(validationErrors.terms)}
                      aria-describedby={validationErrors.terms ? 'terms-error' : undefined}
                      class:field-invalid={Boolean(validationErrors.terms)}
                    />
                    <span class="checkmark"></span>
                  </div>
                  <span class="terms-text">I agree to the <a href="https://www.leave2give.com.au/terms-conditions.html" target="_blank" rel="noopener noreferrer">Leave2Give Terms and Conditions</a></span>
                </label>
                {#if validationErrors.terms}
                  <div class="field-error" id="terms-error" role="alert">{validationErrors.terms[0]}</div>
                {/if}
              </div>
                </div>
              </section>
              
              <div class="step-navigation step-navigation--split">
                <button type="button" class="btn-previous" on:click={previousStep}>
                  Previous
                </button>
                <button type="button" class="btn-next" on:click={() => { if (validateCharityBasicStep()) nextStep(); }}>
                  Next: Additional Details
                </button>
              </div>
            </div>
          {:else if currentStep === 3}
            <p class="step-intro">Optional details to help donors and employers find your charity. You can add these later.</p>
            <div class="charity-step2-fields">
            <section class="form-section" aria-labelledby="charity-contact-heading">
              <h2 id="charity-contact-heading" class="form-section-title">Contact &amp; presence</h2>
              <div class="form-section-body">
            <div class="form-fields-row">
            <div class="form-group">
              <label for="contact_name">Contact Name</label>
              <input 
                type="text" 
                id="contact_name" 
                bind:value={contact_name} 
                disabled={loading}
                placeholder="Contact name (optional)"
                autocomplete="name"
                aria-invalid={Boolean(validationErrors.contact_name)}
                aria-describedby={validationErrors.contact_name ? 'contact_name-error' : undefined}
                class:field-invalid={Boolean(validationErrors.contact_name)}
                on:blur={() => handleFieldBlur('contact_name', contact_name)}
                on:input={() => clearFieldError('contact_name')}
              />
              {#if validationErrors.contact_name}
                <div class="field-error" id="contact_name-error" role="alert">{validationErrors.contact_name[0]}</div>
              {/if}
            </div>
            
            <div class="form-group">
              <label for="contact_email">Contact Email</label>
              <input 
                type="email" 
                id="contact_email" 
                bind:value={contact_email} 
                disabled={loading}
                placeholder="Contact email (optional)"
                autocomplete="email"
                aria-invalid={Boolean(validationErrors.contact_email)}
                aria-describedby={validationErrors.contact_email ? 'contact_email-error' : undefined}
                class:field-invalid={Boolean(validationErrors.contact_email)}
                on:blur={() => handleFieldBlur('contact_email', contact_email)}
                on:input={() => clearFieldError('contact_email')}
              />
              {#if validationErrors.contact_email}
                <div class="field-error" id="contact_email-error" role="alert">{validationErrors.contact_email[0]}</div>
              {/if}
            </div>
            </div>

            <div class="form-fields-row">
            <div class="form-group">
              <label for="contact_phone">Contact Phone</label>
              <input 
                type="tel" 
                id="contact_phone" 
                bind:value={contact_phone} 
                disabled={loading}
                placeholder="Contact phone (optional)"
                autocomplete="tel"
                aria-invalid={Boolean(validationErrors.contact_phone)}
                aria-describedby={validationErrors.contact_phone ? 'contact_phone-error' : undefined}
                class:field-invalid={Boolean(validationErrors.contact_phone)}
                on:blur={() => handleFieldBlur('contact_phone', contact_phone)}
                on:input={() => clearFieldError('contact_phone')}
              />
              {#if validationErrors.contact_phone}
                <div class="field-error" id="contact_phone-error" role="alert">{validationErrors.contact_phone[0]}</div>
              {/if}
            </div>
            
            <div class="form-group">
              <label for="head_office_location">Head Office Location</label>
              <input 
                type="text" 
                id="head_office_location" 
                bind:value={head_office_location} 
                disabled={loading}
                placeholder="Head office location (optional)"
                autocomplete="address-line1"
                aria-invalid={Boolean(validationErrors.head_office_location)}
                aria-describedby={validationErrors.head_office_location ? 'head_office_location-error' : undefined}
                class:field-invalid={Boolean(validationErrors.head_office_location)}
                on:blur={() => handleFieldBlur('head_office_location', head_office_location)}
                on:input={() => clearFieldError('head_office_location')}
              />
              {#if validationErrors.head_office_location}
                <div class="field-error" id="head_office_location-error" role="alert">{validationErrors.head_office_location[0]}</div>
              {/if}
            </div>
            </div>
            
            <div class="form-group">
              <label for="website">Website</label>
              <input 
                type="text" 
                id="website" 
                bind:value={website} 
                disabled={loading}
                placeholder="www.example.com or https://example.com (optional)"
                autocomplete="url"
                aria-invalid={Boolean(validationErrors.website)}
                aria-describedby={validationErrors.website ? 'website-error' : undefined}
                class:field-invalid={Boolean(validationErrors.website)}
                on:blur={() => handleFieldBlur('website', website)}
                on:input={() => clearFieldError('website')}
              />
              {#if validationErrors.website}
                <div class="field-error" id="website-error" role="alert">{validationErrors.website[0]}</div>
              {/if}
            </div>
            
            <div class="form-group">
              <label for="description">Description</label>
              <textarea 
                id="description" 
                bind:value={description} 
                disabled={loading}
                placeholder="Brief description of your charity (optional)"
                rows="3"
                aria-invalid={Boolean(validationErrors.description)}
                aria-describedby={validationErrors.description ? 'description-error' : undefined}
                class:field-invalid={Boolean(validationErrors.description)}
                on:blur={() => handleFieldBlur('description', description)}
                on:input={() => clearFieldError('description')}
              ></textarea>
              {#if validationErrors.description}
                <div class="field-error" id="description-error" role="alert">{validationErrors.description[0]}</div>
              {/if}
            </div>
              </div>
            </section>
            </div>
            
            <!-- Step 2 Navigation -->
            <div class="step-navigation">
              <button type="button" class="btn-previous" on:click={previousStep}>
                Previous
              </button>
            </div>
          {/if}
        {/if}
        
        <!-- Employer Fields -->
        {#if selectedRole === 'employer'}
          {#if currentStep === 1}
            <!-- Step 1: Required Fields + Account Credentials -->
            <div class="form-group">
              <label for="employer_name">Company Name <span class="required" aria-label="required">*</span></label>
              <input 
                type="text" 
                id="employer_name" 
                bind:value={employer_name} 
                required 
                disabled={loading}
                placeholder="Enter company name"
                autocomplete="organization"
                aria-invalid={Boolean(validationErrors.name)}
                aria-describedby={validationErrors.name ? 'employer_name-error' : undefined}
                class:field-invalid={Boolean(validationErrors.name)}
                on:blur={() => handleFieldBlur('employer_name', employer_name)}
                on:input={() => clearFieldError('name')}
              />
              {#if validationErrors.name}
                <div class="field-error" id="employer_name-error" role="alert">{validationErrors.name[0]}</div>
              {/if}
            </div>
            
            <div class="form-group">
              <label for="employer_abn">ABN <span class="required" aria-label="required">*</span></label>
              <input 
                type="text" 
                id="employer_abn" 
                bind:value={employer_abn} 
                required 
                disabled={loading}
                placeholder="11 digits"
                autocomplete="off"
                aria-invalid={Boolean(validationErrors.abn)}
                aria-describedby={validationErrors.abn ? 'employer_abn-error' : undefined}
                class:field-invalid={Boolean(validationErrors.abn)}
                on:blur={() => handleFieldBlur('employer_abn', employer_abn)}
                on:input={() => clearFieldError('abn')}
              />
              <p class="field-hint">
                Look up your ABN at <a href="https://www.abr.business.gov.au/" target="_blank" rel="noopener noreferrer">abr.business.gov.au</a>.
              </p>
              {#if validationErrors.abn}
                <div class="field-error" id="employer_abn-error" role="alert">{validationErrors.abn[0]}</div>
              {/if}
            </div>
            
            <!-- Common Fields for Step 1 -->
            <div class="form-group">
              <label for="email">Email Address <span class="required" aria-label="required">*</span></label>
              <input 
                type="email" 
                id="email" 
                bind:value={email} 
                required 
                disabled={loading}
                placeholder="Enter your email"
                autocomplete="email"
                aria-invalid={Boolean(validationErrors.email)}
                aria-describedby={validationErrors.email ? 'email-error' : undefined}
                class:field-invalid={Boolean(validationErrors.email)}
                on:blur={() => handleFieldBlur('email', email)}
                on:input={() => clearFieldError('email')}
              />
              {#if validationErrors.email}
                <div class="field-error" id="email-error" role="alert">{validationErrors.email[0]}</div>
              {/if}
            </div>
            
            <div class="form-group password-group">
              <label for="password">Create Password <span class="required" aria-label="required">*</span></label>
              <PasswordInput 
                id="password"
                bind:value={password}
                required={true}
                disabled={loading}
                placeholder="At least 8 characters"
                autocomplete="new-password"
                ariaInvalid={Boolean(validationErrors.password)}
                ariaDescribedby={validationErrors.password ? 'password-error' : 'password-help'}
                    className={Boolean(validationErrors.password) ? 'field-invalid' : ''}
                on:blur={() => handleFieldBlur('password', password)}
                on:input={() => clearFieldError('password')}
              />
              <div class="password-help-text" id="password-help">Password must be at least 8 characters long</div>
              {#if validationErrors.password}
                <div class="field-error" id="password-error" role="alert">{validationErrors.password[0]}</div>
              {/if}
            </div>
            
            <div class="form-group password-group">
              <label for="passwordConfirmation">Confirm Password <span class="required" aria-label="required">*</span></label>
              <PasswordInput 
                id="passwordConfirmation"
                bind:value={password_confirmation}
                required={true}
                disabled={loading}
                placeholder="Re-enter password"
                autocomplete="new-password"
                ariaInvalid={Boolean(validationErrors.password_confirmation)}
                ariaDescribedby={validationErrors.password_confirmation ? 'password_confirmation-error' : undefined}
                    className={Boolean(validationErrors.password_confirmation) ? 'field-invalid' : ''}
                on:blur={() => handleFieldBlur('password_confirmation', password_confirmation)}
                on:input={() => clearFieldError('password_confirmation')}
              />
              {#if validationErrors.password_confirmation}
                <div class="field-error" id="password_confirmation-error" role="alert">{validationErrors.password_confirmation[0]}</div>
              {/if}
            </div>
            
            <!-- Terms and Conditions -->
            <div class="form-group terms-group">
              <label class="checkbox-label" for="terms-checkbox-employer">
                <div class="custom-checkbox">
                  <input 
                    type="checkbox" 
                    id="terms-checkbox-employer"
                    bind:checked={terms_and_conditions}
                    disabled={loading}
                    on:change={() => { if (terms_and_conditions) clearFieldError('terms'); }}
                    aria-invalid={Boolean(validationErrors.terms)}
                    aria-describedby={validationErrors.terms ? 'terms-error-employer' : undefined}
                    class:field-invalid={Boolean(validationErrors.terms)}
                  />
                  <span class="checkmark"></span>
                </div>
                <span class="terms-text">I agree to the <a href="https://www.leave2give.com.au/terms-conditions.html" target="_blank" rel="noopener noreferrer">Leave2Give Terms and Conditions</a></span>
              </label>
              {#if validationErrors.terms}
                <div class="field-error" id="terms-error-employer" role="alert">{validationErrors.terms[0]}</div>
              {/if}
            </div>
            
            <!-- Step 1 Navigation -->
            <div class="step-navigation">
              <button type="button" class="btn-next" on:click={() => { if (validateStep1()) nextStep(); }}>
                Next: Additional Details
              </button>
            </div>
          {:else if currentStep === 2}
            <!-- Step 2: Optional Fields -->
            <div class="form-group">
              <label for="employer_contact_name">Contact Name</label>
              <input 
                type="text" 
                id="employer_contact_name" 
                bind:value={employer_contact_name} 
                disabled={loading}
                placeholder="Contact name (optional)"
                autocomplete="name"
                aria-invalid={Boolean(validationErrors.contact_name)}
                aria-describedby={validationErrors.contact_name ? 'employer_contact_name-error' : undefined}
                class:field-invalid={Boolean(validationErrors.contact_name)}
                on:blur={() => handleFieldBlur('employer_contact_name', employer_contact_name)}
                on:input={() => clearFieldError('contact_name')}
              />
              {#if validationErrors.contact_name}
                <div class="field-error" id="employer_contact_name-error" role="alert">{validationErrors.contact_name[0]}</div>
              {/if}
            </div>
            
            <div class="form-group">
              <label for="employer_contact_email">Contact Email</label>
              <input 
                type="email" 
                id="employer_contact_email" 
                bind:value={employer_contact_email} 
                disabled={loading}
                placeholder="Contact email (optional)"
                autocomplete="email"
                aria-invalid={Boolean(validationErrors.contact_email)}
                aria-describedby={validationErrors.contact_email ? 'employer_contact_email-error' : undefined}
                class:field-invalid={Boolean(validationErrors.contact_email)}
                on:blur={() => handleFieldBlur('employer_contact_email', employer_contact_email)}
                on:input={() => clearFieldError('contact_email')}
              />
              {#if validationErrors.contact_email}
                <div class="field-error" id="employer_contact_email-error" role="alert">{validationErrors.contact_email[0]}</div>
              {/if}
            </div>
            
            <div class="form-group">
              <label for="employer_contact_phone">Contact Phone</label>
              <input 
                type="tel" 
                id="employer_contact_phone" 
                bind:value={employer_contact_phone} 
                disabled={loading}
                placeholder="Contact phone (optional)"
                autocomplete="tel"
                aria-invalid={Boolean(validationErrors.contact_phone)}
                aria-describedby={validationErrors.contact_phone ? 'employer_contact_phone-error' : undefined}
                class:field-invalid={Boolean(validationErrors.contact_phone)}
                on:blur={() => handleFieldBlur('employer_contact_phone', employer_contact_phone)}
                on:input={() => clearFieldError('contact_phone')}
              />
              {#if validationErrors.contact_phone}
                <div class="field-error" id="employer_contact_phone-error" role="alert">{validationErrors.contact_phone[0]}</div>
              {/if}
            </div>
            
            <div class="form-group">
              <label for="employer_head_office_location">Head Office Location</label>
              <input 
                type="text" 
                id="employer_head_office_location" 
                bind:value={employer_head_office_location} 
                disabled={loading}
                placeholder="Head office location (optional)"
                autocomplete="address-line1"
                aria-invalid={Boolean(validationErrors.head_office_location)}
                aria-describedby={validationErrors.head_office_location ? 'employer_head_office_location-error' : undefined}
                class:field-invalid={Boolean(validationErrors.head_office_location)}
                on:blur={() => handleFieldBlur('employer_head_office_location', employer_head_office_location)}
                on:input={() => clearFieldError('head_office_location')}
              />
              {#if validationErrors.head_office_location}
                <div class="field-error" id="employer_head_office_location-error" role="alert">{validationErrors.head_office_location[0]}</div>
              {/if}
            </div>
            
            <!-- Step 2 Navigation -->
            <div class="step-navigation">
              <button type="button" class="btn-previous" on:click={previousStep}>
                Previous
              </button>
            </div>
          {/if}
        {/if}

        <!-- Register Button - Show only for employee or on final step for charity/employer -->
        {#if selectedRole === 'employee' || (selectedRole === 'employer' && currentStep === 2) || (selectedRole === 'charity' && currentStep === 3)}
          <div class="actions-signup">
            <button type="submit" disabled={loading || !selectedRole || !terms_and_conditions} class="register-btn">
              {loading ? 'Registering...' : 'Register'}
            </button>
          </div>
        {/if}
        
        <div class="login-link">
          Already have an account? <a href="/login" on:click|preventDefault={() => push('/login')}>Login</a>
        </div>
      </form>
    {/if}
  </div>
</main>

<Notification 
  bind:show={notificationShow}
  type={notificationType}
  message={notificationMessage}
  duration={2000}
  onDismiss={() => { notificationShow = false; }}
/>

<style>
  /* Signup page design tokens — warm humanitarian editorial */
  main {
    --signup-surface: #fffdf9;
    --signup-surface-muted: #faf8f4;
    --signup-ink: #1f2937;
    --signup-ink-muted: #6b7280;
    --signup-border: rgba(0, 0, 0, 0.08);
    --signup-shadow: 0 4px 24px rgba(52, 58, 64, 0.08);

    width: 100%;
    min-height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #f5f3ef;
    background-image: url('/background.png');
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    padding: 1rem;
  }
  
  @media (max-width: 768px) {
    main {
      align-items: flex-start;
      padding: 0.5rem;
      padding-top: 1rem;
    }
  }
  
  main.registration-page {
    align-items: flex-start;
    padding: clamp(2.5rem, 12vh, 5rem) 1rem 2rem;
    box-sizing: border-box;
    overflow-y: auto;
    max-height: 100vh;
  }

  @media (max-width: 768px) {
    main.registration-page {
      padding: 1.25rem 0.5rem 1.5rem;
    }
  }
  
  .logo-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 1.5rem;
  }

  .registration-header {
    margin-bottom: 1.125rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  .registration-back-link {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    margin-bottom: 0.875rem;
    padding: 0.25rem 0;
    background: none;
    border: none;
    color: var(--signup-ink-muted);
    font-size: 0.8125rem;
    font-weight: 500;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: color 0.2s ease, transform 0.15s ease;
    font-family: inherit;
  }

  .registration-back-link:hover:not(:disabled) {
    color: var(--color-secondary);
    transform: translateX(-2px);
  }

  .registration-back-link:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .registration-back-link:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(42, 157, 143, 0.2);
    border-radius: 4px;
  }

  .registration-header-brand {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .registration-header-logo-wrap {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
    border-radius: 14px;
    background: var(--signup-surface-muted);
    border: 1px solid var(--signup-border);
  }

  .registration-header-logo {
    height: 60px;
    width: auto;
    display: block;
  }

  .registration-header-text h1 {
    margin: 0 0 0.375rem;
    text-align: left;
    font-size: 1.625rem;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: var(--signup-ink);
  }

  .registration-step-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin: 0;
    line-height: 1.4;
  }

  .registration-step-pill {
    display: inline-flex;
    align-items: center;
    padding: 0.1875rem 0.625rem;
    border-radius: 9999px;
    background: rgba(255, 180, 0, 0.16);
    border: 1px solid rgba(255, 180, 0, 0.28);
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #7c5e00;
  }

  .registration-step-label {
    font-size: 0.875rem;
    color: var(--signup-ink-muted);
    font-weight: 500;
  }

  .registration-progress {
    margin-top: 0.875rem;
    height: 5px;
    background: rgba(0, 0, 0, 0.06);
    border-radius: 9999px;
    overflow: hidden;
  }

  .registration-progress-fill {
    height: 100%;
    background: var(--color-primary);
    border-radius: 9999px;
    transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  @keyframes signup-card-enter {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .logo-image {
    height: 60px;
    width: auto;
  }
  
  @media (max-width: 768px) {
    .logo-container {
      margin-bottom: 1rem;
    }
    
    .logo-image {
      height: 50px;
    }
  }
  
  .form-container {
    max-width: 800px;
    width: 100%;
    background-color: white;
    padding: 2rem;
    border-radius: 16px;
    box-shadow: var(--signup-shadow);
    overflow: visible;
  }

  .registration-active {
    max-width: 620px;
    padding: 1.375rem 1.625rem 1.5rem;
    background: var(--signup-surface);
    border: 1px solid var(--signup-border);
    animation: signup-card-enter 0.42s ease-out both;
    overflow: visible;
  }
  
  @media (max-width: 768px) {
    .form-container {
      padding: 1rem;
      display: flex;
      flex-direction: column;
      overflow: visible;
    }
    
    /* Specific optimizations for employee form on small screens */
    .employee-form-container h1 {
      font-size: 1.125rem;
      margin-bottom: 0.125rem;
    }
    
    .employee-form-container .subtitle {
      display: none;
    }
    
    .registration-active {
      padding: 1rem 1.125rem 1.25rem;
    }

    .registration-header {
      margin-bottom: 0.875rem;
      padding-bottom: 0.75rem;
    }

    .registration-header-logo-wrap {
      padding: 0.375rem;
      border-radius: 12px;
    }

    .registration-header-logo {
      height: 48px;
    }

    .registration-header-text h1 {
      font-size: 1.3125rem;
    }

    .registration-step-label {
      font-size: 0.8125rem;
    }
    
    .employee-form-fields .form-group {
      margin-bottom: 0.875rem;
    }
    
    .password-fields-row {
      gap: 0.875rem;
      margin-bottom: 0.875rem;
    }
    
    .password-fields-row .form-group {
      margin-bottom: 0;
    }
  }
  
  @media (max-width: 479px) {
    .password-fields-row {
      grid-template-columns: 1fr;
    }
  }
  
  /* Role Selection Styles */
  .role-selection {
    width: 100%;
  }
  
  .role-selection-title {
    text-align: center;
    color: var(--color-text-dark);
    font-size: 1.125rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
  }
  
  .role-tiles {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
  }
  
  .role-tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 1.5rem;
    background-color: white;
    border: 2px solid var(--color-border);
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: center;
    font-family: inherit;
  }
  
  .role-tile:hover:not(:disabled) {
    border-color: var(--color-primary);
    background-color: rgba(255, 180, 0, 0.05);
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
  
  .role-tile:active:not(:disabled) {
    transform: translateY(0);
  }
  
  .role-tile:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  .role-tile-icon {
    color: var(--color-primary);
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .role-tile-title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-text-dark);
    margin: 0 0 0.5rem 0;
  }
  
  .role-tile-description {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
    line-height: 1.4;
  }
  
  .step-navigation {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 1.25rem;
    gap: 1rem;
  }

  .multi-step-form .step-navigation {
    margin-top: 1rem;
  }

  .step-navigation--split .btn-next {
    margin-left: 0;
  }

  .step-intro {
    margin: 0 0 1rem;
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    color: var(--signup-ink-muted);
    line-height: 1.5;
    background: rgba(42, 157, 143, 0.06);
    border-left: 3px solid var(--color-secondary);
    border-radius: 0 8px 8px 0;
  }

  .btn-next, .btn-previous {
    padding: 0.75rem 1.5rem;
    border-radius: 25rem;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease;
    font-family: inherit;
    border: none;
  }
  
  .btn-next {
    background-color: var(--color-primary);
    color: var(--color-text-dark);
    margin-left: auto;
    box-shadow: 0 2px 8px rgba(255, 180, 0, 0.3);
  }
  
  .btn-next:hover:not(:disabled) {
    background-color: var(--color-primary-hover);
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(255, 180, 0, 0.35);
  }

  .btn-next:active:not(:disabled) {
    transform: translateY(0);
  }
  
  .btn-previous {
    background-color: white;
    color: var(--signup-ink);
    border: 1px solid rgba(0, 0, 0, 0.1);
  }
  
  .btn-previous:hover:not(:disabled) {
    background-color: var(--signup-surface-muted);
    border-color: var(--color-secondary);
    color: var(--color-secondary);
  }
  
  .btn-next:disabled, .btn-previous:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (min-width: 768px) {
    .role-tiles {
      grid-template-columns: repeat(3, 1fr);
    }
  }
  
  h1 {
    color: var(--color-text-dark);
    text-align: center;
    margin-bottom: 0.5rem;
    font-weight: 600;
  }
  
  .subtitle {
    text-align: center;
    color: #6b7280;
    margin-bottom: 2rem;
    font-size: 0.875rem;
  }
  
  /* Employee form specific header adjustments */
  .employee-form-container h1 {
    margin-bottom: 0.25rem;
  }
  
  .employee-form-container .subtitle {
    margin-bottom: 1rem;
  }
  
  /* Password fields side by side */
  .password-fields-row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 0;
  }
  
  @media (min-width: 480px) {
    .password-fields-row {
      grid-template-columns: 1fr 1fr;
      gap: 0.875rem;
    }
    
    .password-fields-row .form-group label {
      font-size: 0.8125rem;
    }
  }
  
  @media (min-width: 640px) {
    .password-fields-row {
      gap: 1rem;
    }
    
    .password-fields-row .form-group label {
      font-size: 0.875rem;
    }
  }
  
  .employee-form-fields {
    display: flex;
    flex-direction: column;
    gap: 0;
  }
  
  .form-group {
    margin-bottom: 1.5rem;
  }

  .multi-step-form .form-group {
    margin-bottom: 0.875rem;
  }
  
  /* Employee form field spacing */
  form.employee-form .employee-form-fields .form-group {
    margin-bottom: 1rem;
  }
  
  form.employee-form .password-fields-row {
    margin-bottom: 1rem;
  }
  
  form.employee-form .password-fields-row .form-group {
    margin-bottom: 0;
  }
  
  form.employee-form .password-fields-row .field-error {
    margin-top: 0.25rem;
  }

  /* Form sections — editorial grouping */
  .form-section {
    margin-bottom: 1.25rem;
  }

  .form-section:last-child {
    margin-bottom: 0;
  }

  .form-section-title {
    margin: 0 0 0.75rem;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--color-secondary);
  }

  .form-section-body {
    padding: 1rem 1.125rem;
    background: var(--signup-surface-muted);
    border: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 12px;
    overflow: visible;
  }

  .form-section-body .form-group:last-child,
  .form-section-body .password-fields-row:last-child {
    margin-bottom: 0;
  }

  .form-section-body .field-hint-full {
    margin-bottom: 0;
  }

  .form-section-body .terms-group {
    margin-top: 0.25rem;
    margin-bottom: 0;
    padding: 0.75rem 0.875rem;
    background: white;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.05);
  }

  /* Charity registration form layout */
  .charity-step1-fields .password-fields-row {
    margin-bottom: 0.875rem;
  }

  .charity-step1-fields .password-fields-row .form-group {
    margin-bottom: 0;
  }

  .charity-step1-fields .terms-group {
    margin-bottom: 0.5rem;
  }

  .form-fields-row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .form-fields-row .form-group {
    margin-bottom: 0;
  }

  @media (min-width: 640px) {
    .form-fields-row {
      grid-template-columns: 1fr 1fr;
      gap: 0.875rem;
    }
  }

  .charity-step1-fields .form-group,
  .charity-step2-fields .form-group {
    margin-bottom: 0.875rem;
  }

  .charity-dgr-step-fields .step-navigation,
  .charity-step1-fields .step-navigation,
  .charity-identity-block {
    margin-bottom: 0.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  .charity-identity-row {
    display: flex;
    gap: 0.875rem;
    align-items: flex-start;
    margin-bottom: 0.375rem;
  }

  .charity-logo-picker {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
  }

  .charity-logo-thumb {
    width: 76px;
    height: 76px;
    border-radius: 12px;
    border: 2px dashed rgba(42, 157, 143, 0.35);
    background: white;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.1875rem;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--color-secondary);
    padding: 0.25rem;
    transition: border-color 0.2s ease, background-color 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
    font-family: inherit;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }

  .charity-logo-thumb:hover:not(:disabled) {
    border-color: var(--color-primary);
    background: #fffbeb;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 180, 0, 0.15);
  }

  .charity-logo-thumb.has-logo {
    border-style: solid;
    border-color: rgba(255, 180, 0, 0.4);
    padding: 0.3125rem;
  }

  .charity-logo-thumb img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 6px;
  }

  .charity-logo-thumb:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .charity-logo-thumb:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.25);
  }

  .charity-logo-remove {
    padding: 0;
    border: none;
    background: none;
    color: #6b7280;
    font-size: 0.6875rem;
    cursor: pointer;
    font-family: inherit;
    text-decoration: underline;
  }

  .charity-logo-remove:hover:not(:disabled) {
    color: var(--color-negative);
  }

  .charity-name-field {
    flex: 1;
    min-width: 0;
    margin-bottom: 0;
  }

  .field-hint-inline {
    margin: 0;
    font-size: 0.75rem;
  }

  .charity-identity-block + .form-group {
    margin-top: 1rem;
  }

  .logo-file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  
  @media (max-width: 768px) {
    h1 {
      font-size: 1.25rem;
      margin-bottom: 0.25rem;
    }
    
    .subtitle {
      font-size: 0.75rem;
      margin-bottom: 1rem;
    }
    
    .form-group {
      margin-bottom: 1rem;
    }
    
    /* Already handled above with general rule */
  }
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }
  
  @media (max-width: 768px) {
    label {
      margin-bottom: 0.375rem;
      font-size: 0.8125rem;
    }
  }
  
  .required {
    color: var(--color-negative);
  }
  
  input, select, textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    outline: none;
    font-family: inherit;
    box-sizing: border-box;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  
  @media (max-width: 768px) {
    input, select, textarea {
      padding: 0.625rem 0.875rem;
      font-size: 0.875rem;
    }
  }
  
  input.field-invalid, select.field-invalid, textarea.field-invalid {
    border: 1px solid var(--color-negative) !important;
    box-shadow: 0 0 0 1px rgba(252, 108, 107, 0.15);
  }
  
  input::placeholder, textarea::placeholder {
    color: rgba(0, 0, 0, 0.25);
  }
  
  input:focus, select:focus, textarea:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.15);
  }
  
  input.field-invalid:focus, select.field-invalid:focus, textarea.field-invalid:focus {
    border: 1px solid var(--color-negative) !important;
    box-shadow: 0 0 0 3px rgba(252, 108, 107, 0.2);
  }
  
  input:disabled, select:disabled, textarea:disabled {
    background-color: #f3f4f6 !important;
    color: black !important;
    cursor: not-allowed;
    opacity: 0.7;
  }

  textarea {
    resize: vertical;
    min-height: 80px;
  }
  
  /* Select Dropdown Styling */
  .select-wrapper {
    position: relative;
  }
  
  .select-wrapper::after {
    content: '';
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid #6b7280;
    pointer-events: none;
  }
  
  select {
    appearance: none;
    background-image: none;
    padding-right: 2.5rem;
  }
  
  select:disabled {
    background-color: #f3f4f6 !important;
  }

  .password-group {
    position: relative;
  }
  
  .password-help-text {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.25rem;
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    .password-help-text {
      font-size: 0.6875rem;
    }
  }
  
  .form-error-banner {
    background-color: var(--color-negative-bg);
    color: var(--color-negative);
    padding: 0.75rem;
    border-radius: 4px;
    margin-bottom: 1rem;
  }
  
  .field-error {
    display: block;
    color: var(--color-negative);
    font-size: 0.75rem;
    font-weight: 500;
    margin-top: 0.375rem;
    line-height: 1.4;
  }

  .form-group:has(.field-invalid) label,
  .form-group:has(.field-error) label {
    color: #991b1b;
  }

  .field-hint {
    font-size: 0.875rem;
    color: var(--color-unselected);
    margin-top: 0.25rem;
    margin-bottom: 0;
  }

  .field-hint a {
    color: var(--color-link);
  }

  .field-hint-full {
    margin-top: -0.5rem;
    margin-bottom: 1rem;
  }

  .charity-dgr-intro {
    margin-top: 0;
    margin-bottom: 1rem;
  }

  .dgr-ineligible-notice {
    margin-top: 1rem;
    padding: 1rem 1.125rem;
    border-radius: 8px;
    background-color: #fee2e2;
    border: 1px solid #fecaca;
    color: #991b1b;
    font-size: 0.875rem;
    line-height: 1.5;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .dgr-ineligible-notice__lead {
    font-weight: 600;
    margin: 0;
  }

  .dgr-ineligible-notice p {
    margin: 0;
  }

  .dgr-ineligible-notice ul {
    margin: 0;
    padding-left: 1.25rem;
  }

  .dgr-ineligible-notice li + li {
    margin-top: 0.25rem;
  }

  .dgr-ineligible-notice__footer {
    margin-top: 0.25rem;
    color: #7f1d1d;
  }
  
  @media (max-width: 768px) {
    .field-error {
      font-size: 0.6875rem;
      margin-top: 0.25rem;
    }
  }
  
  .actions-signup {
    width: 100%;
    margin-top: 1.5rem;
  }
  
  .register-btn {
    width: 100%;
    padding: 0.9rem;
    background-color: var(--color-primary);
    color: var(--color-text-dark);
    border: none;
    border-radius: 25rem;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 600;
    transition: background-color 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease;
    box-shadow: 0 2px 10px rgba(255, 180, 0, 0.3);
  }
  
  @media (max-width: 768px) {
    .actions-signup {
      margin-top: 1rem;
    }
    
    .register-btn {
      padding: 0.75rem;
      font-size: 0.9375rem;
    }
  }
  
  .register-btn:hover:not(:disabled) {
    background-color: var(--color-primary-hover);
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(255, 180, 0, 0.35);
  }

  .register-btn:active:not(:disabled) {
    transform: translateY(0);
  }
  
  .register-btn:disabled {
    background-color: var(--color-disabled-bg);
    cursor: not-allowed;
  }
  
  .login-link {
    text-align: center;
    margin-top: 1.5rem;
    color: #6b7280;
    font-size: 0.875rem;
  }
  
  @media (max-width: 768px) {
    .login-link {
      margin-top: 1rem;
      font-size: 0.8125rem;
    }
  }
  
  a {
    color: var(--color-primary);
    text-decoration: none;
    font-weight: 500;
  }
  
  a:hover {
    text-decoration: underline;
  }

  @media (prefers-reduced-motion: reduce) {
    .registration-active {
      animation: none;
    }

    .registration-progress-fill,
    .registration-back-link,
    .charity-logo-thumb,
    .btn-next,
    .btn-previous,
    .register-btn {
      transition: none;
    }

    .btn-next:hover:not(:disabled),
    .register-btn:hover:not(:disabled),
    .charity-logo-thumb:hover:not(:disabled),
    .registration-back-link:hover:not(:disabled) {
      transform: none;
    }
  }

  .terms-group {
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 768px) {
    .terms-group {
      margin-top: 1rem;
      margin-bottom: 1rem;
    }
  }

  .checkbox-label {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    cursor: pointer;
    font-weight: normal;
    position: relative;
  }

  .custom-checkbox {
    position: relative;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  .custom-checkbox input[type="checkbox"] {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }

  .checkmark {
    position: absolute;
    top: 0;
    left: 0;
    height: 20px;
    width: 20px;
    border: 2px solid #E5E7EB;
    border-radius: 4px;
    transition: all 0.2s ease;
  }

  .custom-checkbox input[type="checkbox"]:checked ~ .checkmark {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
  }

  .custom-checkbox input[type="checkbox"]:disabled ~ .checkmark {
    background-color: var(--color-disabled-bg);
    border-color: var(--color-disabled-bg);
  }

  .checkmark:after {
    content: "";
    position: absolute;
    display: none;
    left: 6px;
    top: 2px;
    width: 4px;
    height: 9px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  .custom-checkbox input[type="checkbox"]:checked ~ .checkmark:after {
    display: block;
  }

  .custom-checkbox input[type="checkbox"].field-invalid ~ .checkmark {
    border-color: var(--color-negative);
  }

  .terms-text {
    flex: 1;
    line-height: 1.4;
    font-size: 0.875rem;
    padding-top: 2px;
  }
  
  @media (max-width: 768px) {
    .terms-text {
      font-size: 0.75rem;
      line-height: 1.3;
    }
    
    .custom-checkbox {
      width: 18px;
      height: 18px;
    }
    
    .checkmark {
      height: 18px;
      width: 18px;
    }
    
    .checkbox-label {
      gap: 0.5rem;
    }
    
    form.employee-form .terms-group {
      margin-top: 0.875rem;
      margin-bottom: 0.875rem;
    }
  }

  .terms-text a {
    color: var(--color-primary);
    text-decoration: none;
    font-weight: 500;
  }

  .terms-text a:hover {
    text-decoration: underline;
  }
</style> 